import React, { useState, useEffect } from 'react';
import { api } from '@/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BarChart3, Scale, AlertTriangle, Gavel, Handshake, FileText, CheckSquare, Lock, Clock } from 'lucide-react';

const STATUS_COLORS = {
  potential_dispute: 'bg-amber-100 text-amber-700', internal_escalation: 'bg-orange-100 text-orange-700',
  negotiation_active: 'bg-blue-100 text-blue-700', formal_dispute: 'bg-red-100 text-red-700',
  referral_in_preparation: 'bg-purple-100 text-purple-700', forum_active: 'bg-indigo-100 text-indigo-700',
  awaiting_decision: 'bg-cyan-100 text-cyan-700', decision_issued: 'bg-teal-100 text-teal-700',
  compliance_pending: 'bg-amber-100 text-amber-700', settled: 'bg-green-100 text-green-700',
  closed: 'bg-slate-100 text-slate-600', archived: 'bg-slate-50 text-slate-400',
};
const RISK_COLORS = { low: 'bg-green-100 text-green-700', medium: 'bg-amber-100 text-amber-700', high: 'bg-red-100 text-red-700', critical: 'bg-red-600 text-white' };

export default function DisputeReporting({ projectId }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.disputes.getReport(projectId);
        setReport(res.data);
      } catch { /* silent */ } finally { setLoading(false); }
    })();
  }, [projectId]);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;
  if (!report) return null;

  const fmt = (v) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 }).format(v || 0);

  return (
    <div className="space-y-5" data-testid="dispute-reporting">
      <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">Dispute Portfolio Report</h2>

      {/* Key Financials */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-l-4 border-l-slate-800"><CardContent className="p-4">
          <p className="text-[10px] uppercase text-slate-500">Total Matters</p>
          <p className="text-2xl font-bold text-slate-800">{report.total_matters}</p>
        </CardContent></Card>
        <Card className="border-l-4 border-l-red-500"><CardContent className="p-4">
          <p className="text-[10px] uppercase text-slate-500">Total Exposure</p>
          <p className="text-2xl font-bold text-red-600">{fmt(report.total_amount_in_dispute)}</p>
        </CardContent></Card>
        <Card className="border-l-4 border-l-green-500"><CardContent className="p-4">
          <p className="text-[10px] uppercase text-slate-500">Settled Amount</p>
          <p className="text-2xl font-bold text-green-600">{fmt(report.settled_amount)}</p>
        </CardContent></Card>
        <Card className="border-l-4 border-l-blue-500"><CardContent className="p-4">
          <p className="text-[10px] uppercase text-slate-500">Awarded Amount</p>
          <p className="text-2xl font-bold text-blue-600">{fmt(report.awarded_amount)}</p>
        </CardContent></Card>
      </div>

      {/* Activity Counts */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {[
          { label: 'Issues', value: report.total_issues, icon: Scale, color: 'text-blue-600' },
          { label: 'Notices', value: report.total_notices, icon: FileText, color: 'text-purple-600' },
          { label: 'Evidence', value: report.total_evidence, icon: Lock, color: 'text-amber-600' },
          { label: 'Submissions', value: report.total_submissions, icon: FileText, color: 'text-cyan-600' },
          { label: 'Settlements', value: report.total_settlements, icon: Handshake, color: 'text-green-600' },
          { label: 'Decisions', value: report.total_decisions, icon: Gavel, color: 'text-indigo-600' },
        ].map(item => {
          const Icon = item.icon;
          return (
            <Card key={item.label}><CardContent className="p-3 text-center">
              <Icon className={`h-4 w-4 mx-auto mb-1 ${item.color}`} />
              <p className="text-xl font-bold">{item.value}</p>
              <p className="text-[9px] uppercase text-slate-500">{item.label}</p>
            </CardContent></Card>
          );
        })}
      </div>

      {/* Overdue Deadlines Warning */}
      {report.overdue_deadlines > 0 && (
        <Card className="border-red-200"><CardContent className="p-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-500" />
          <span className="text-sm font-medium text-red-700">{report.overdue_deadlines} overdue deadline{report.overdue_deadlines > 1 ? 's' : ''}</span>
        </CardContent></Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* By Status */}
        <Card><CardContent className="p-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">By Status</h3>
          <div className="space-y-1.5">
            {Object.entries(report.by_status || {}).filter(([, v]) => v > 0).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between py-0.5">
                <Badge className={`text-[10px] ${STATUS_COLORS[status] || 'bg-slate-100'}`}>{status.replace(/_/g, ' ')}</Badge>
                <span className="font-bold text-sm">{count}</span>
              </div>
            ))}
          </div>
        </CardContent></Card>

        {/* By Risk */}
        <Card><CardContent className="p-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">By Risk</h3>
          <div className="space-y-1.5">
            {Object.entries(report.by_risk || {}).filter(([, v]) => v > 0).map(([risk, count]) => (
              <div key={risk} className="flex items-center justify-between py-0.5">
                <Badge className={`text-[10px] ${RISK_COLORS[risk] || 'bg-slate-100'}`}>{risk}</Badge>
                <span className="font-bold text-sm">{count}</span>
              </div>
            ))}
          </div>
        </CardContent></Card>

        {/* By Forum */}
        <Card><CardContent className="p-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">By Forum</h3>
          <div className="space-y-1.5">
            {Object.entries(report.by_forum || {}).filter(([, v]) => v > 0).map(([forum, count]) => (
              <div key={forum} className="flex items-center justify-between py-0.5">
                <Badge variant="outline" className="text-[10px] capitalize">{forum.replace(/_/g, ' ')}</Badge>
                <span className="font-bold text-sm">{count}</span>
              </div>
            ))}
          </div>
        </CardContent></Card>
      </div>

      {/* Matters Table */}
      {report.matters?.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">All Matters</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="report-matters-table">
                <thead className="border-b">
                  <tr>
                    <th className="text-left px-2 py-2 text-[10px] font-bold uppercase text-slate-500">Ref</th>
                    <th className="text-left px-2 py-2 text-[10px] font-bold uppercase text-slate-500">Title</th>
                    <th className="text-left px-2 py-2 text-[10px] font-bold uppercase text-slate-500">Status</th>
                    <th className="text-left px-2 py-2 text-[10px] font-bold uppercase text-slate-500">Risk</th>
                    <th className="text-left px-2 py-2 text-[10px] font-bold uppercase text-slate-500">Amount</th>
                    <th className="text-left px-2 py-2 text-[10px] font-bold uppercase text-slate-500">Counterparty</th>
                  </tr>
                </thead>
                <tbody>
                  {report.matters.map(m => (
                    <tr key={m.id} className="border-b last:border-b-0">
                      <td className="px-2 py-2 font-mono text-xs text-slate-400">{m.matter_ref}</td>
                      <td className="px-2 py-2 text-xs text-slate-800 max-w-[200px] truncate">{m.title}</td>
                      <td className="px-2 py-2"><Badge className={`text-[9px] ${STATUS_COLORS[m.status]}`}>{m.status?.replace(/_/g, ' ')}</Badge></td>
                      <td className="px-2 py-2"><Badge className={`text-[9px] ${RISK_COLORS[m.risk_rating]}`}>{m.risk_rating}</Badge></td>
                      <td className="px-2 py-2 text-xs font-mono">{m.amount_in_dispute ? fmt(m.amount_in_dispute) : '-'}</td>
                      <td className="px-2 py-2 text-xs text-slate-500">{m.counterparty || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
